/**
 * AgentActivityPanel — live journal event feed for AgentFlow IDE.
 *
 * Features:
 *  - Real-time updates via GET /api/projects/journal/stream (SSE)
 *  - Per-agent color coding
 *  - Filter by agent name and event type
 *  - Pause / resume button (buffers events while paused)
 *  - Connection status indicator
 *  - Failure events highlighted in red
 */
import { useState, useMemo, useCallback } from 'react';
import { Text } from '@qwickapps/react-framework';
import { useJournalStream, type LiveJournalEntry } from './useSSE';

// ── Constants ─────────────────────────────────────────────────────────────────

const AGENT_COLORS: Record<string, string> = {
  forge: '#2196f3',
  runner: '#9c27b0',
  'team-lead': '#4caf50',
  devops: '#ff9800',
  system: '#607d8b',
  reviewer: '#009688',
  tester: '#8bc34a',
  raaj: '#e91e63',
};

const FAILURE_KEYWORDS = ['blocked', 'error', 'failed', 'failure', 'degraded'];

// Well-known event types for the filter dropdown
const KNOWN_EVENTS = [
  'goal_created',
  'item_created',
  'item_completed',
  'item_blocked',
  'item_updated',
  'comment_added',
  'agent_heartbeat',
  'pr_reviewed',
  'build_failed',
  'health_check_fail',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getAgentColor(agent: string): string {
  const lower = agent.toLowerCase();
  for (const [key, color] of Object.entries(AGENT_COLORS)) {
    if (lower.includes(key)) return color;
  }
  // Deterministic hash → hue for unknown agents
  let hash = 0;
  for (let i = 0; i < lower.length; i++) hash = (hash * 31 + lower.charCodeAt(i)) >>> 0;
  return `hsl(${hash % 360}, 55%, 45%)`;
}

function isFailureEvent(event: string): boolean {
  const lower = event.toLowerCase();
  return FAILURE_KEYWORDS.some(k => lower.includes(k));
}

function formatRelativeTime(isoString: string): string {
  const diffSec = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function summarizePayload(payload: Record<string, unknown>): string {
  const str = JSON.stringify(payload);
  return str.length > 100 ? str.slice(0, 97) + '…' : str;
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ConnectionDotProps {
  isConnected: boolean;
  isPaused: boolean;
}

function ConnectionDot({ isConnected, isPaused }: ConnectionDotProps) {
  const color = isPaused ? '#ff9800' : isConnected ? '#4caf50' : '#f44336';
  const label = isPaused ? 'paused' : isConnected ? 'live' : 'disconnected';
  return (
    <span
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        color: 'var(--theme-text-secondary)',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          boxShadow: isConnected && !isPaused ? `0 0 0 2px ${color}33` : 'none',
          animation: isConnected && !isPaused ? 'pulse-dot 2s ease-in-out infinite' : 'none',
        }}
      />
      {label}
    </span>
  );
}

interface EntryRowProps {
  entry: LiveJournalEntry;
}

function EntryRow({ entry }: EntryRowProps) {
  const failure = isFailureEvent(entry.event);
  const agentColor = getAgentColor(entry.agent);

  return (
    <div
      style={{
        padding: '7px 10px',
        borderRadius: 6,
        background: failure ? 'rgba(244,67,54,0.06)' : 'transparent',
        border: `1px solid ${failure ? '#f44336' : 'var(--theme-border-color)'}`,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {/* Agent badge */}
        <span
          style={{
            display: 'inline-block',
            padding: '1px 7px',
            borderRadius: 10,
            background: agentColor,
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.02em',
            flexShrink: 0,
          }}
        >
          {entry.agent}
        </span>

        {/* Event badge */}
        <span
          style={{
            fontSize: 12,
            color: failure ? '#f44336' : 'var(--theme-text-primary)',
            fontFamily: 'monospace',
          }}
        >
          {entry.event}
        </span>

        {/* Timestamp — pushed right */}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: 'var(--theme-text-disabled)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {formatRelativeTime(entry.ts)}
        </span>
      </div>

      {/* Payload summary */}
      {entry.payload && Object.keys(entry.payload).length > 0 && (
        <Text
          variant="caption"
          content={summarizePayload(entry.payload)}
          customColor="var(--theme-text-secondary)"
        />
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const PANEL_STYLES = `
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
`;

export default function AgentActivityPanel() {
  const [agentFilter, setAgentFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [agentInput, setAgentInput] = useState('');

  // Pass filters to the hook so the server applies them (reduces bandwidth).
  const activeAgentFilter = agentFilter || undefined;
  const activeEventFilter = eventFilter || undefined;

  const { entries, isConnected, isPaused, error, pause, resume, clear } = useJournalStream({
    agent: activeAgentFilter,
    eventType: activeEventFilter,
    maxEntries: 200,
    historyCount: 30,
  });

  // Derive unique agents seen for the dropdown
  const seenAgents = useMemo(() => {
    const names = new Set<string>();
    entries.forEach(e => names.add(e.agent));
    return Array.from(names).sort();
  }, [entries]);

  const handleAgentFilterCommit = useCallback(() => {
    setAgentFilter(agentInput.trim());
  }, [agentInput]);

  const togglePause = useCallback(() => {
    if (isPaused) resume(); else pause();
  }, [isPaused, pause, resume]);

  return (
    <>
      <style>{PANEL_STYLES}</style>
      <div
        style={{
          padding: 20,
          borderRadius: 8,
          background: 'var(--theme-bg-surface)',
          border: '1px solid var(--theme-border-color)',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text variant="h6" content="Activity Feed" />
          <ConnectionDot isConnected={isConnected} isPaused={isPaused} />
          {isPaused && (
            <span
              style={{
                fontSize: 11,
                padding: '1px 7px',
                borderRadius: 10,
                background: 'rgba(255,152,0,0.15)',
                color: '#ff9800',
                border: '1px solid #ff9800',
              }}
            >
              buffering…
            </span>
          )}

          <div style={{ flex: 1 }} />

          {/* Pause / Resume */}
          <button
            onClick={togglePause}
            aria-label={isPaused ? 'Resume stream' : 'Pause stream'}
            title={isPaused ? 'Resume stream' : 'Pause stream'}
            style={{
              padding: '4px 12px',
              borderRadius: 4,
              border: '1px solid var(--theme-border-color)',
              background: 'transparent',
              color: 'var(--theme-text-primary)',
              cursor: 'pointer',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span className="material-icons" style={{ fontSize: 14 }}>
              {isPaused ? 'play_arrow' : 'pause'}
            </span>
            {isPaused ? 'Resume' : 'Pause'}
          </button>

          {/* Clear */}
          <button
            onClick={clear}
            aria-label="Clear feed"
            title="Clear all entries"
            style={{
              padding: '4px 8px',
              borderRadius: 4,
              border: '1px solid var(--theme-border-color)',
              background: 'transparent',
              color: 'var(--theme-text-secondary)',
              cursor: 'pointer',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span className="material-icons" style={{ fontSize: 14 }}>clear_all</span>
          </button>
        </div>

        {/* ── Filters ── */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Agent filter */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flex: '1 1 180px' }}>
            <label
              htmlFor="activity-agent-select"
              style={{ fontSize: 11, color: 'var(--theme-text-secondary)', whiteSpace: 'nowrap' }}
            >
              Agent:
            </label>
            <select
              id="activity-agent-select"
              value={agentFilter}
              onChange={e => { setAgentFilter(e.target.value); setAgentInput(''); }}
              style={{
                flex: 1,
                fontSize: 12,
                padding: '3px 6px',
                borderRadius: 4,
                border: '1px solid var(--theme-border-color)',
                background: 'var(--theme-bg-surface)',
                color: 'var(--theme-text-primary)',
              }}
            >
              <option value="">All agents</option>
              {seenAgents.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            {/* Manual agent type-in */}
            <input
              type="text"
              value={agentInput}
              onChange={e => setAgentInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAgentFilterCommit(); }}
              onBlur={handleAgentFilterCommit}
              placeholder="Type agent…"
              aria-label="Filter by agent name"
              style={{
                flex: 1,
                minWidth: 80,
                fontSize: 12,
                padding: '3px 6px',
                borderRadius: 4,
                border: '1px solid var(--theme-border-color)',
                background: 'var(--theme-bg-surface)',
                color: 'var(--theme-text-primary)',
              }}
            />
          </div>

          {/* Event type filter */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flex: '1 1 160px' }}>
            <label
              htmlFor="activity-event-select"
              style={{ fontSize: 11, color: 'var(--theme-text-secondary)', whiteSpace: 'nowrap' }}
            >
              Event:
            </label>
            <select
              id="activity-event-select"
              value={eventFilter}
              onChange={e => setEventFilter(e.target.value)}
              style={{
                flex: 1,
                fontSize: 12,
                padding: '3px 6px',
                borderRadius: 4,
                border: '1px solid var(--theme-border-color)',
                background: 'var(--theme-bg-surface)',
                color: 'var(--theme-text-primary)',
              }}
            >
              <option value="">All events</option>
              {KNOWN_EVENTS.map(ev => (
                <option key={ev} value={ev}>{ev}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div
            role="alert"
            style={{
              padding: '8px 12px',
              borderRadius: 4,
              background: 'rgba(244,67,54,0.08)',
              border: '1px solid #f44336',
            }}
          >
            <Text variant="caption" content={error} customColor="#f44336" />
          </div>
        )}

        {/* ── Entry count ── */}
        {entries.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--theme-text-disabled)' }}>
            {entries.length} event{entries.length !== 1 ? 's' : ''}
            {activeAgentFilter && ` · agent: ${activeAgentFilter}`}
            {activeEventFilter && ` · event: ${activeEventFilter}`}
          </div>
        )}

        {/* ── Entries list (newest first) ── */}
        <div
          data-testid="activity-feed-list"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            maxHeight: 520,
            overflowY: 'auto',
          }}
        >
          {entries.length === 0 && !error && (
            <Text
              variant="body2"
              content={isConnected ? 'Waiting for events…' : 'Connecting…'}
              customColor="var(--theme-text-secondary)"
            />
          )}
          {[...entries].reverse().map(entry => (
            <EntryRow key={`${entry.id}-${entry.ts}`} entry={entry} />
          ))}
        </div>
      </div>
    </>
  );
}
